import { useState, useEffect, useMemo, useCallback } from 'react'
import { IoRefresh } from 'react-icons/io5'
import { usePlayerSOTStats, usePlayerFoulsCardsStats } from '../../swr-config'
import { PlayerStatsFilterBar, type PlayerStatsFilters } from './PlayerStatsFilterBar'
import { PlayerStatsTable } from './PlayerStatsTable'
import { PlayerStatsCard } from './PlayerStatsCard'
import { PlayerFoulsCardsTable } from './PlayerFoulsCardsTable'
import { PlayerFoulsCardsCard } from './PlayerFoulsCardsCard'
import { useFeatureTracking, FeatureNames } from '../../hooks/useActivityTracking'
import { useLeague, isLiveTodayMode, LEAGUES, COUNTRY_FLAGS, type League } from '../../context/LeagueContext'
import type { PlayerStatsTab } from '../../types'

const DEFAULT_SHOTS_FILTERS: PlayerStatsFilters = {
  daysAhead: 7,
  leagueIds: [],
  fixtureId: null,
  position: null,
  minProbability: null,
  sortBy: 'probability',
  sortOrder: 'desc',
}

const DEFAULT_FOULS_FILTERS: PlayerStatsFilters = {
  daysAhead: 7,
  leagueIds: [],
  fixtureId: null,
  position: null,
  minProbability: null,
  sortBy: 'fouls_committed',
  sortOrder: 'desc',
}

const STORAGE_KEY = 'statscout_player_stats_filters'
const TAB_STORAGE_KEY = 'statscout_player_stats_tab'
const LIMIT = 50

function loadFiltersFromStorage(tab: PlayerStatsTab): PlayerStatsFilters {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY}_${tab}`)
    if (stored) {
      const parsed = JSON.parse(stored)
      const defaults = tab === 'shots' ? DEFAULT_SHOTS_FILTERS : DEFAULT_FOULS_FILTERS
      return { ...defaults, ...parsed }
    }
  } catch {
    // Invalid JSON or localStorage error - use defaults
  }
  return tab === 'shots' ? DEFAULT_SHOTS_FILTERS : DEFAULT_FOULS_FILTERS
}

function loadTabFromStorage(): PlayerStatsTab {
  try {
    const stored = localStorage.getItem(TAB_STORAGE_KEY)
    if (stored === 'shots' || stored === 'fouls_cards') {
      return stored
    }
  } catch {
    // localStorage error
  }
  return 'shots'
}

export function PlayerStatsPage() {
  // Track feature usage
  useFeatureTracking(FeatureNames.PLAYER_STATS)

  const { currentLeague } = useLeague()
  const isLiveToday = isLiveTodayMode(currentLeague)

  // Get the initial league: use currentLeague if it's a regular league, otherwise default to EPL
  const getInitialLeague = (): League => {
    if (!isLiveToday && currentLeague.type === 'league') {
      return currentLeague
    }
    return LEAGUES[0] // Default to EPL
  }

  const [selectedLeague, setSelectedLeague] = useState<League>(getInitialLeague)

  // Sync selectedLeague when global league changes (from sidebar)
  useEffect(() => {
    if (!isLiveToday && currentLeague.type === 'league') {
      setSelectedLeague(currentLeague)
    }
  }, [currentLeague, isLiveToday])

  const [activeTab, setActiveTab] = useState<PlayerStatsTab>(loadTabFromStorage)
  const [shotsFilters, setShotsFiltersState] = useState<PlayerStatsFilters>(() => loadFiltersFromStorage('shots'))
  const [foulsFilters, setFoulsFiltersState] = useState<PlayerStatsFilters>(() => loadFiltersFromStorage('fouls_cards'))
  const [shotsPage, setShotsPage] = useState(0)
  const [foulsPage, setFoulsPage] = useState(0)

  // Get current filters and page based on active tab
  const filters = activeTab === 'shots' ? shotsFilters : foulsFilters
  const page = activeTab === 'shots' ? shotsPage : foulsPage
  const setPage = activeTab === 'shots' ? setShotsPage : setFoulsPage

  // Persist tab to localStorage
  const handleTabChange = useCallback((tab: PlayerStatsTab) => {
    setActiveTab(tab)
    try {
      localStorage.setItem(TAB_STORAGE_KEY, tab)
    } catch {
      // localStorage error
    }
  }, [])

  // Persist filters to localStorage
  const setFilters = useCallback((newFilters: PlayerStatsFilters | ((prev: PlayerStatsFilters) => PlayerStatsFilters)) => {
    const setFiltersState = activeTab === 'shots' ? setShotsFiltersState : setFoulsFiltersState
    setFiltersState((prev) => {
      const resolved = typeof newFilters === 'function' ? newFilters(prev) : newFilters
      try {
        localStorage.setItem(`${STORAGE_KEY}_${activeTab}`, JSON.stringify(resolved))
      } catch {
        // localStorage full or unavailable
      }
      return resolved
    })
  }, [activeTab])

  // Build SWR params for shots - use selected league
  const shotsSWRParams = useMemo(() => ({
    daysAhead: shotsFilters.daysAhead,
    leagueIds: [selectedLeague.api_league_id],
    fixtureId: shotsFilters.fixtureId,
    position: shotsFilters.position,
    minProbability: shotsFilters.minProbability,
    sortBy: shotsFilters.sortBy,
    sortOrder: shotsFilters.sortOrder,
    limit: LIMIT,
    offset: shotsPage * LIMIT,
  }), [shotsFilters, shotsPage, selectedLeague.api_league_id])

  // Build SWR params for fouls/cards - use selected league
  const foulsSWRParams = useMemo(() => ({
    daysAhead: foulsFilters.daysAhead,
    leagueIds: [selectedLeague.api_league_id],
    fixtureId: foulsFilters.fixtureId,
    position: foulsFilters.position,
    minProbability: foulsFilters.minProbability,
    sortBy: foulsFilters.sortBy as 'fouls_committed' | 'fouls_drawn' | 'cards' | 'tackles' | 'kickoff' | 'ref_factor',
    sortOrder: foulsFilters.sortOrder,
    limit: LIMIT,
    offset: foulsPage * LIMIT,
  }), [foulsFilters, foulsPage, selectedLeague.api_league_id])

  // SWR hooks - both tabs fetch data but only active one is displayed
  const shotsData = usePlayerSOTStats(shotsSWRParams)
  const foulsData = usePlayerFoulsCardsStats(foulsSWRParams)

  // Get current data based on active tab
  const { data, error, isLoading, mutate } = activeTab === 'shots' ? shotsData : foulsData

  // Reset page when filters or league change
  useEffect(() => {
    if (activeTab === 'shots') {
      setShotsPage(0)
    } else {
      setFoulsPage(0)
    }
  }, [
    activeTab,
    selectedLeague.api_league_id,
    filters.daysAhead,
    filters.fixtureId,
    filters.position,
    filters.minProbability,
    filters.sortBy,
    filters.sortOrder,
  ])

  // Handle sort column click
  const handleSortChange = (column: string) => {
    if (filters.sortBy === column) {
      // Toggle sort order
      setFilters({
        ...filters,
        sortOrder: filters.sortOrder === 'desc' ? 'asc' : 'desc',
      })
    } else {
      // New column, default to desc
      setFilters({
        ...filters,
        sortBy: column as PlayerStatsFilters['sortBy'],
        sortOrder: 'desc',
      })
    }
  }

  const totalPages = data ? Math.ceil(data.total_count / LIMIT) : 0
  const errorMessage = error ? (error instanceof Error ? error.message : String(error)) : null

  const tabDescription = activeTab === 'shots'
    ? `${selectedLeague.name} player shots-on-target predictions`
    : `${selectedLeague.name} player fouls, cards, and tackles predictions`

  return (
    <div className="player-stats-page">
      <div className="card">
        <div className="card-inner">
          <div className="page-header">
            <div className="page-header-main">
              <h1>Player Stats</h1>
              <button
                className="refresh-btn"
                onClick={() => mutate()}
                disabled={isLoading}
                title="Refresh"
              >
                <IoRefresh size={18} className={isLoading ? 'spinning' : ''} />
              </button>
            </div>
            <p className="page-subtitle">{tabDescription}</p>
          </div>

          {/* League selector - always shown */}
          <div className="page-league-selector">
            <span className="page-league-selector-label">Select League:</span>
            <div className="page-league-selector-chips">
              {LEAGUES.filter(l => l.type === 'league').map((league) => (
                <button
                  key={league.api_league_id}
                  className={`chip ${selectedLeague.api_league_id === league.api_league_id ? 'chip-active' : ''}`}
                  onClick={() => setSelectedLeague(league)}
                >
                  <span className="chip-flag">{COUNTRY_FLAGS[league.country]}</span>
                  {league.shortName}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Selector */}
          <div className="player-stats-tabs">
            <button
              className={`tab ${activeTab === 'shots' ? 'active' : ''}`}
              onClick={() => handleTabChange('shots')}
            >
              Shots
            </button>
            <button
              className={`tab ${activeTab === 'fouls_cards' ? 'active' : ''}`}
              onClick={() => handleTabChange('fouls_cards')}
            >
              Fouls & Cards
            </button>
          </div>

          <PlayerStatsFilterBar filters={filters} onChange={setFilters} activeTab={activeTab} />

          {/* Loading state */}
          {isLoading && !data && (
            <div className="skeleton-loading-container">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="skeleton-card" style={{ height: '60px', marginBottom: '8px' }} />
              ))}
            </div>
          )}

          {/* Error state */}
          {errorMessage && (
            <div className="error-state">
              <p>Error loading player stats: {errorMessage}</p>
              <button onClick={() => mutate()}>Retry</button>
            </div>
          )}

          {/* Data display */}
          {!errorMessage && data && (
            <>
              <div className="results-summary">
                Showing {data.items.length} of {data.total_count} players
              </div>

              {/* Shots tab content */}
              {activeTab === 'shots' && (
                <>
                  {/* Table view for larger screens */}
                  <PlayerStatsTable
                    items={shotsData.data.items}
                    sortBy={shotsFilters.sortBy}
                    sortOrder={shotsFilters.sortOrder}
                    onSortChange={handleSortChange}
                  />

                  {/* Card view for mobile */}
                  <div className="player-stats-list mobile-only">
                    {shotsData.data.items.map((item) => (
                      <PlayerStatsCard key={`${item.fixture_id}-${item.player_id}`} item={item} />
                    ))}
                  </div>
                </>
              )}

              {/* Fouls & Cards tab content */}
              {activeTab === 'fouls_cards' && (
                <>
                  {/* Table view for larger screens */}
                  <PlayerFoulsCardsTable
                    items={foulsData.data.items}
                    sortBy={foulsFilters.sortBy}
                    sortOrder={foulsFilters.sortOrder}
                    onSortChange={handleSortChange}
                  />

                  {/* Card view for mobile */}
                  <div className="player-stats-list mobile-only">
                    {foulsData.data.items.map((item) => (
                      <PlayerFoulsCardsCard key={`${item.fixture_id}-${item.player_id}`} item={item} />
                    ))}
                  </div>
                </>
              )}

              {/* Empty state */}
              {data.items.length === 0 && (
                <div className="empty-state">
                  <p>No player predictions match your filters.</p>
                  <p>Try adjusting your filters or check back later.</p>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                    Previous
                  </button>
                  <span>
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
